import { useRef, useState } from "react";
import cx from "classnames";

import { useClickOutside } from "common/hooks";
import Angle from "common/components/Angle";
import { Optional } from "common/utils/types";

import TextChip from "../content/TextChip";

import styles from "../../styles/RichSelect.module.less";

type RichSelectOptionProps<T> = {
  option: Optional<T>;
  isActive: boolean;
  renderOption: (value: Optional<T>) => React.ReactNode;
  onChange: (option: T) => void;
};

const RichSelectOption = <T extends any>({
  option,
  isActive,
  renderOption,
  onChange,
}: RichSelectOptionProps<T>) => {
  return (
    <button type="button" className={styles.option} onClick={() => option && onChange(option)}>
      <div className={styles.listOption}>{renderOption(option)}</div>
      {isActive && (
        <div className={styles.chip}>
          <TextChip size="s">Selected</TextChip>
        </div>
      )}
    </button>
  );
};

type RichSelectProps<T> = {
  onChange: (value: T) => void;
  renderOption: (value: Optional<T>) => React.ReactNode;
  value: Optional<T>;
  options: T[];
  getKey: (value: T) => string | number;
};

const RichSelect = <T extends object>(props: RichSelectProps<T>) => {
  const { renderOption, value, options, onChange, getKey } = props;
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useClickOutside(containerRef, () => setIsDropdownVisible(false));

  const handleChange = (option: T) => {
    onChange(option);
    setIsDropdownVisible(false);
  };

  return (
    <div ref={containerRef} className={cx(styles.container, isDropdownVisible && styles.open)}>
      <button
        type="button"
        className={styles.select}
        onClick={() => setIsDropdownVisible((v) => !v)}
      >
        <div className={styles.selectedOption}>{renderOption(value)}</div>
        <Angle direction="down" className={styles.icon} isActive={isDropdownVisible} />
      </button>
      <div className={styles.popover}>
        {options.map((option) => (
          <RichSelectOption
            key={getKey(option)}
            option={option}
            isActive={!!(value && getKey(value) === getKey(option))}
            renderOption={renderOption}
            onChange={handleChange}
          />
        ))}
      </div>
    </div>
  );
};

export default RichSelect;
